import React, { createContext, useEffect, useState } from "react";

const CART_STORAGE_KEY = "cartItems";
const MAX_QUANTITY = 99;

const emptyCartContext = {
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
};

export const CartContext = createContext(emptyCartContext);

const normalizeOption = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeProductId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeQuantity = (value) => {
  const quantity = Number(value);

  if (!Number.isFinite(quantity) || quantity <= 0) return null;

  return Math.min(MAX_QUANTITY, Math.max(1, Math.floor(quantity)));
};

const isSameCartItem = (item, id, variant, packaging) =>
  item.id === id &&
  item.variant === variant &&
  item.packaging === packaging;

const sanitizeCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return items;
    }

    const id = normalizeProductId(entry.id);
    const quantity = normalizeQuantity(entry.quantity);

    if (id === null || quantity === null) return items;

    // size/color were used by the previous cart schema. Keeping the fallback
    // here lets existing visitors retain their cart after the data migration.
    const variant = normalizeOption(entry.variant ?? entry.size);
    const packaging = normalizeOption(entry.packaging ?? entry.color);
    const existingIndex = items.findIndex((item) =>
      isSameCartItem(item, id, variant, packaging)
    );

    if (existingIndex === -1) {
      return [...items, { id, variant, packaging, quantity }];
    }

    return items.map((item, index) =>
      index === existingIndex
        ? {
            ...item,
            quantity: Math.min(MAX_QUANTITY, item.quantity + quantity),
          }
        : item
    );
  }, []);
};

const loadCartItems = () => {
  try {
    const savedCartItems = window.localStorage.getItem(CART_STORAGE_KEY);
    return savedCartItems ? sanitizeCartItems(JSON.parse(savedCartItems)) : [];
  } catch (error) {
    console.warn("Unable to restore the shopping cart.", error);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(loadCartItems);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify(sanitizeCartItems(cartItems))
      );
    } catch (error) {
      console.warn("Unable to save the shopping cart.", error);
    }
  }, [cartItems]);

  const addToCart = (
    productId,
    quantity = 1,
    variant = "",
    packaging = ""
  ) => {
    const id = normalizeProductId(productId);
    const amount = normalizeQuantity(quantity);

    if (id === null || amount === null) return;

    const normalizedVariant = normalizeOption(variant);
    const normalizedPackaging = normalizeOption(packaging);

    setCartItems((previousItems) => {
      const safeItems = sanitizeCartItems(previousItems);
      const existingItem = safeItems.find((item) =>
        isSameCartItem(
          item,
          id,
          normalizedVariant,
          normalizedPackaging
        )
      );

      if (!existingItem) {
        return [
          ...safeItems,
          {
            id,
            variant: normalizedVariant,
            packaging: normalizedPackaging,
            quantity: amount,
          },
        ];
      }

      return safeItems.map((item) =>
        isSameCartItem(item, id, normalizedVariant, normalizedPackaging)
          ? {
              ...item,
              quantity: Math.min(MAX_QUANTITY, item.quantity + amount),
            }
          : item
      );
    });
  };

  const removeFromCart = (productId, variant = "", packaging = "") => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    const normalizedVariant = normalizeOption(variant);
    const normalizedPackaging = normalizeOption(packaging);

    setCartItems((previousItems) =>
      sanitizeCartItems(previousItems).filter(
        (item) =>
          !isSameCartItem(
            item,
            id,
            normalizedVariant,
            normalizedPackaging
          )
      )
    );
  };

  const updateQuantity = (
    productId,
    variant = "",
    packaging = "",
    quantity = 1
  ) => {
    const id = normalizeProductId(productId);
    const amount = normalizeQuantity(quantity);

    if (id === null || amount === null) return;

    const normalizedVariant = normalizeOption(variant);
    const normalizedPackaging = normalizeOption(packaging);

    setCartItems((previousItems) =>
      sanitizeCartItems(previousItems).map((item) =>
        isSameCartItem(item, id, normalizedVariant, normalizedPackaging)
          ? { ...item, quantity: amount }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
